import React, { useCallback } from 'react';
import { StyleSheet, View } from 'react-native';
import { useStyle } from 'react-native-style-utilities';
import {
  BodyText,
  Button,
  Chip,
  FONTS,
  Form,
  HStack,
  Modal,
  TextField,
  theme,
  useFluidSize,
  useResponsive,
} from '@sweetgreen/garnish';

import type { TranslationKeys } from '@order/Localization';
import { useLocalizationContext } from '@order/Localization';

import type {
  AddressField,
  AddressTypeProps,
  DeliveryNoteProps,
  FooterProps,
  FormFieldProps,
} from '../AddressForm.types';
import { DELIVERY_PREFERENCE } from '../constants';
import { TooltipChip } from './AddressForm.components.tooltipChip';

export const DeliveryNotes = (props: DeliveryNoteProps) => {
  const { form, notes, maxLength } = props;

  const { t } = useLocalizationContext();

  return (
    <View style={styles.deliveryNotes}>
      <View style={styles.header}>
        <BodyText style={styles.text}>
          {t('addresses.placeholder.delivery-note-a11y-label')}
        </BodyText>
        <BodyText
          style={[
            notes?.length === maxLength && styles.cautionText,
            styles.text,
            styles.smallText,
          ]}
        >
          {notes?.length}/{maxLength}
        </BodyText>
      </View>

      <Form.TextField
        name="notes"
        control={form.control}
        testID="account.address-form.delivery-notes"
        style={styles.inputField}
        maxLength={maxLength}
        placeholder={t('addresses.placeholder.delivery-note')}
        accessibilityLabel={t('addresses.placeholder.delivery-note-a11y-label')}
        accessibilityHint={t('addresses.placeholder.delivery-note-a11y-label')}
        multiline
        blurOnSubmit
        returnKeyType="done"
      />
    </View>
  );
};

export const DeliveryPreference = ({ form, updateField }: FormFieldProps) => {
  const { t } = useLocalizationContext();
  const { currentBreakpoint } = useResponsive();
  const fluid = useFluidSize({ min: 375, max: 425 });
  const paddingHorizontal = fluid(12, 16);
  const fluidPadding = useStyle(
    () => ({ paddingHorizontal }),
    [paddingHorizontal],
  );

  return (
    <View style={currentBreakpoint.isXS ? styles.mobileSections : undefined}>
      <BodyText style={styles.text}>{t('addresses.drop-off')}</BodyText>

      <Chip.Menu
        value={form.deliveryPreference}
        onChange={updateField('deliveryPreference')}
      >
        <Chip
          value={DELIVERY_PREFERENCE.LEAVE_AT_DOOR}
          height={36}
          style={fluidPadding}
          testID="account.address-form.leave-at-door"
          accessibilityHint={t('addresses.delivery-preference.leave-at-door')}
          accessibilityLabel={t('addresses.delivery-preference.leave-at-door')}
        >
          {t('addresses.delivery-preference.leave-at-door')}
        </Chip>

        <Chip
          value={DELIVERY_PREFERENCE.DOOR}
          height={36}
          style={fluidPadding}
          testID="account.address-form.meet-at-door"
          accessibilityHint={t('addresses.delivery-preference.meet-at-door')}
          accessibilityLabel={t('addresses.delivery-preference.meet-at-door')}
        >
          {t('addresses.delivery-preference.meet-at-door')}
        </Chip>

        <Chip
          value={DELIVERY_PREFERENCE.CURBSIDE}
          height={36}
          style={fluidPadding}
          testID="account.address-form.meet-outside"
          accessibilityHint={t('addresses.delivery-preference.meet-outside')}
          accessibilityLabel={t('addresses.delivery-preference.meet-outside')}
        >
          {t('addresses.delivery-preference.meet-outside')}
        </Chip>
      </Chip.Menu>
    </View>
  );
};

export const AddressType = (props: AddressTypeProps) => {
  const {
    addressType,
    hasHomeAddress,
    hasWorkAddress,
    updateField,
    setAddressType,
  } = props;

  const { t } = useLocalizationContext();

  const shouldDisableHomeChip = addressType !== 'home' && hasHomeAddress;
  const shouldDisableWorkChip = addressType !== 'work' && hasWorkAddress;

  const handleOnChange = useCallback(
    (type: string) => {
      const validType = ['home', 'work'].includes(type) ? type : '';

      updateField('name')(validType);
      setAddressType(type);
    },
    [setAddressType, updateField],
  );

  return (
    <Chip.Menu value={addressType} onChange={handleOnChange}>
      <TooltipChip
        value="home"
        height={36}
        showTooltip={shouldDisableHomeChip}
        accessibilityLabel={t('addresses.address-type.home')}
        accessibilityHint={t('addresses.address-type.home')}
        testID="account.address-form.address-type.home"
      >
        <BodyText>{t('addresses.address-type.home')}</BodyText>
      </TooltipChip>

      <TooltipChip
        value="work"
        height={36}
        showTooltip={shouldDisableWorkChip}
        accessibilityLabel={t('addresses.address-type.work')}
        accessibilityHint={t('addresses.address-type.work')}
        testID="account.address-form.address-type.work"
      >
        <BodyText>{t('addresses.address-type.work')}</BodyText>
      </TooltipChip>

      <Chip
        value="custom"
        height={36}
        accessibilityLabel={t('addresses.address-type.custom')}
        accessibilityHint={t('addresses.address-type.custom')}
        testID="account.address-form.address-type.custom"
      >
        {t('addresses.address-type.custom')}
      </Chip>
    </Chip.Menu>
  );
};

export const CustomNameField = (props: CustomNameFieldProps) => {
  const { name, customNameNotice, isCustomNameInvalid, updateField } = props;

  const { t } = useLocalizationContext();

  return (
    <View style={styles.customName}>
      <TextField
        testID="account.address-form.custom-name"
        accessibilityLabel={t('addresses.custom-name')}
        accessibilityHint={t('addresses.custom-name')}
        label={t('addresses.custom-name')}
        maxLength={20}
        value={name}
        invalid={isCustomNameInvalid}
        notice={customNameNotice ? t(customNameNotice) : undefined}
        onChangeText={updateField('name')}
      />
    </View>
  );
};

export const Footer = (props: FooterProps) => {
  const {
    loading,
    editing,
    deleting,
    canSubmit,
    submitCta,
    submitForm,
    onDelete,
  } = props;

  const { t } = useLocalizationContext();
  const canDelete = Boolean(editing && onDelete);

  return (
    <Modal.Footer style={styles.footer} withoutTopBorder>
      <HStack
        itemsPerRow={canDelete ? 2 : 1}
        customSpacing={theme.spacing['4']}
      >
        {canDelete ? (
          <Button
            size="large"
            palette="caution"
            isLoading={deleting}
            onPress={onDelete}
            testID="account.address-form.delete-button"
            accessibilityLabel={t('addresses.remove-address.label')}
            accessibilityHint={t('addresses.remove-address.hint')}
            accessibilityRole="button"
          >
            {t('addresses.remove-address.label')}
          </Button>
        ) : null}

        <Button
          size="large"
          isLoading={loading}
          onPress={submitForm}
          disabled={!canSubmit}
          testID="account.address-form.save-button"
          accessibilityLabel={t('addresses.save.a11y')}
          accessibilityHint={t('addresses.save.a11y')}
          accessibilityRole="button"
        >
          {submitCta ?? t('addresses.save')}
        </Button>
      </HStack>
    </Modal.Footer>
  );
};

// ─── Types ─────────────────────────────────────────────────────────────

type CustomNameFieldProps = {
  readonly name?: string;
  readonly customNameNotice?: TranslationKeys;
  readonly isCustomNameInvalid?: boolean;
  readonly updateField: (key: AddressField) => (value: string) => void;
};

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingBottom: theme.spacing['1'],
  },
  inputField: {
    height: 80,
  },
  text: {
    fontSize: 12,
    lineHeight: theme.spacing['4'],
    paddingBottom: theme.spacing['2'],
    fontFamily: FONTS.SWEET_SANS_TEXT,
  },
  deliveryNotes: {
    paddingTop: theme.spacing['8'],
  },
  customName: {
    paddingTop: theme.spacing['4'],
  },
  mobileSections: {
    paddingTop: theme.spacing['4'],
  },
  smallText: {
    fontSize: 10,
  },
  cautionText: {
    color: theme.colors.CAUTION,
  },
  footer: {
    flexDirection: 'row',
    padding: theme.spacing['6'],
    paddingTop: theme.spacing['6'],
  },
});
